import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ocorrenciaService } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';
import OcorrenciaTags from './OcorrenciaTags';
import Button from '../ui/Button';
import ConfirmModal from '../ui/ConfirmModal';
import './Ocorrencias.css';

/**
 * Sessão 24 — Módulo Ocorrências MVP (TD104).
 *
 * Detalhe + ações da ocorrência:
 *   - Arquivar / Monitorar (PATCH /status)
 *   - Promover pra NC (POST /promover) — confirmação via ConfirmModal (#204)
 *
 * Se status='promovida_nc', mostra link pra NC criada.
 */

const STATUS_LABELS = {
  em_triagem: 'Em triagem',
  arquivada: 'Arquivada',
  monitorada: 'Monitorada',
  promovida_nc: 'Promovida → NC',
};

const STATUS_CLASSES = {
  em_triagem: 'oc-badge-triagem',
  arquivada: 'oc-badge-arquivada',
  monitorada: 'oc-badge-monitorada',
  promovida_nc: 'oc-badge-promovida',
};

const ORIGEM_LABELS = {
  reclamacao_cliente: 'Reclamação cliente',
  desvio_interno: 'Desvio interno',
  auditoria: 'Auditoria',
  fornecedor: 'Fornecedor',
  outro: 'Outro',
};

// #156 — trilha de auditoria: rótulo + ícone (Material Symbol) por tipo de evento
const TIPO_LABELS = {
  criada: 'Criada',
  editada: 'Editada',
  status: 'Status alterado',
  promovida: 'Promovida pra NC',
  excluida: 'Excluída',
};
const TIPO_ICONS = {
  criada: 'add_circle',
  editada: 'edit',
  status: 'swap_horiz',
  promovida: 'arrow_upward',
  excluida: 'delete',
};

function OcorrenciaDetail({ ocId, onBack, onPromovida, onDeleted }) {
  const [oc, setOc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  // #212 — edição inline da ocorrência (PATCH /api/v3/ocorrencia/{id}, já
  // existente no backend; bloqueado 409 se promovida). Antes a única "edição"
  // possível era excluir e recriar.
  const [editando, setEditando] = useState(false);
  const [editData, setEditData] = useState(null);

  // #204 follow-up — confirmação estilizada no lugar do window.confirm
  // (a NC migrou no lote anterior; aqui são promover/excluir da ocorrência).
  const [confirmando, setConfirmando] = useState(null); // 'promover' | 'excluir' | null

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await ocorrenciaService.get(ocId);
      setOc(data);
    } catch (err) {
      showError(`Erro ao carregar ocorrência: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocId]);

  const handleMudarStatus = async (novoStatus) => {
    if (acting) return;
    setActing(true);
    try {
      const updated = await ocorrenciaService.mudarStatus(ocId, novoStatus);
      setOc(updated);
      showSuccess(`Status atualizado: ${STATUS_LABELS[novoStatus]}`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      showError(`Erro: ${detail}`);
    } finally {
      setActing(false);
    }
  };

  // #204: a confirmação (copy do #203) agora vive no ConfirmModal — este
  // handler é só a execução, disparada pelo "Promover" do modal.
  const handlePromover = async () => {
    if (acting) return;
    setActing(true);
    try {
      const result = await ocorrenciaService.promover(ocId);
      setConfirmando(null);
      toast.success(`Ocorrência promovida! NC criada: ${result.numero_nc || `#${result.nc_id}`}`, {
        toastId: 'oc-promote-success',
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
      if (onPromovida) onPromovida(result.nc_id);
      else carregar();  // refresh local
    } catch (err) {
      setConfirmando(null);
      const detail = err.response?.data?.detail || err.message;
      showError(`Erro ao promover: ${detail}`);
    } finally {
      setActing(false);
    }
  };

  // #212 — handlers da edição inline
  const iniciarEdicao = () => {
    setEditData({
      descricao: oc.descricao || '',
      origem: oc.origem || 'desvio_interno',
      cliente_reclamante: oc.cliente_reclamante || '',
      produto: oc.produto || '',
      lote: oc.lote || '',
      data_ocorrencia: oc.data_ocorrencia || '',
      contexto_processo: oc.contexto_processo || '',
    });
    setEditando(true);
  };

  const handleSalvarEdicao = async () => {
    if (acting) return;
    if (!editData.descricao.trim()) {
      showError('A descrição é obrigatória.');
      return;
    }
    setActing(true);
    try {
      // PATCH semântico: só envia o que mudou (campo opcional limpo vai como
      // null pra apagar; descrição nunca vai vazia — validada acima).
      const payload = {};
      ['descricao', 'origem', 'cliente_reclamante', 'produto', 'lote', 'data_ocorrencia', 'contexto_processo']
        .forEach((k) => {
          const novo = (editData[k] ?? '').toString().trim();
          const atual = (oc[k] ?? '').toString();
          if (novo !== atual) payload[k] = novo || null;
        });
      if (Object.keys(payload).length === 0) {
        setEditando(false);
        return;
      }
      const updated = await ocorrenciaService.editar(ocId, payload);
      setOc(updated);
      setEditando(false);
      showSuccess('Ocorrência atualizada.');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      if (status === 409) {
        showError('Ocorrência promovida pra NC não pode ser editada — edite a NC vinculada.');
      } else {
        showError(`Erro ao salvar: ${detail}`);
      }
    } finally {
      setActing(false);
    }
  };

  const handleExcluir = async () => {
    if (acting) return;
    setActing(true);
    try {
      await ocorrenciaService.excluir(ocId);
      setConfirmando(null);
      showSuccess('Ocorrência excluída.');
      if (onDeleted) onDeleted();
      else if (onBack) onBack();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      if (status === 409) {
        showError('Ocorrência promovida pra NC não pode ser excluída. Delete a NC primeiro.');
      } else {
        showError(`Erro ao excluir: ${detail}`);
      }
    } finally {
      setActing(false);
    }
  };

  if (loading) return <p className="oc-loading">Carregando ocorrência...</p>;
  if (!oc) return <p className="oc-loading">Ocorrência não encontrada.</p>;

  const formatDate = (iso, withTime = false) => {
    if (!iso) return '—';
    try {
      // #211: date-only ("2026-06-08") parseado cru vira meia-noite UTC →
      // -3h no Brasil = exibia o DIA ANTERIOR (digitou 08/06, via 07/06).
      // Âncora em meia-noite LOCAL pra campos só-data (data_ocorrencia).
      const d = /^\d{4}-\d{2}-\d{2}$/.test(String(iso))
        ? new Date(iso + 'T00:00:00')
        : new Date(iso);
      return withTime
        ? d.toLocaleString('pt-BR')
        : d.toLocaleDateString('pt-BR');
    } catch {
      return iso;
    }
  };

  // #156 — prettify do detalhe do evento de status (mapeia chaves cruas → rótulos)
  const prettifyDetalhe = (ev) => {
    if (!ev || !ev.detalhe) return '';
    if (ev.tipo === 'status' && ev.detalhe.includes('→')) {
      const partes = ev.detalhe.split('→').map((s) => s.trim());
      if (partes.length === 2) {
        return `${STATUS_LABELS[partes[0]] || partes[0]} → ${STATUS_LABELS[partes[1]] || partes[1]}`;
      }
    }
    return ev.detalhe;
  };

  const isPromovida = oc.status_triagem === 'promovida_nc';
  const isEmTriagem = oc.status_triagem === 'em_triagem';

  return (
    <div className="oc-detail-container">
      <div className="oc-detail-header">
        <Button variant="secondary" size="sm" icon="arrow_back" onClick={onBack}>
          Voltar à lista
        </Button>
        <h2>{oc.numero_ocorrencia || `Ocorrência #${oc.id}`}</h2>
        <span className={`oc-badge ${STATUS_CLASSES[oc.status_triagem] || ''}`}>
          {STATUS_LABELS[oc.status_triagem] || oc.status_triagem}
        </span>
        {/* Editar/Excluir só disponíveis se não promovida — backend bloqueia 409 */}
        {!isPromovida && !editando && (
          <Button
            variant="secondary"
            size="sm"
            icon="edit"
            onClick={iniciarEdicao}
            disabled={acting}
            title="Editar campos da ocorrência"
          >
            Editar
          </Button>
        )}
        {!isPromovida && (
          <Button
            variant="danger"
            size="sm"
            icon="delete"
            onClick={() => setConfirmando('excluir')}
            disabled={acting}
            title="Excluir ocorrência (soft-delete)"
          >
            Excluir
          </Button>
        )}
      </div>

      {isPromovida && oc.nc_id && (
        <div className="oc-detail-promovida-banner">
          <strong>
            <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '4px' }}>check_circle</span>
            Promovida pra NC:
          </strong>{' '}
          <button
            type="button"
            className="oc-btn-link"
            onClick={() => onPromovida && onPromovida(oc.nc_id)}
          >
            Abrir NC #{oc.nc_id}
          </button>
        </div>
      )}

      <div className="oc-detail-body">
        {/* #212 — modo edição: substitui Identificação/Descrição/Contexto
            pelo form (mesmas classes do OcorrenciaForm). Tags/Ações/Histórico
            continuam visíveis abaixo. */}
        {editando && (
          <section className="oc-detail-section">
            <h3>
              <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>edit</span>
              Editar ocorrência
            </h3>
            <div className="oc-form">
              <div className="oc-form-group">
                <label htmlFor="oc-edit-descricao">Descrição *</label>
                <textarea
                  id="oc-edit-descricao"
                  rows={4}
                  value={editData.descricao}
                  onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                />
              </div>
              <div className="oc-form-row">
                <div className="oc-form-group">
                  <label htmlFor="oc-edit-origem">Origem *</label>
                  <select
                    id="oc-edit-origem"
                    value={editData.origem}
                    onChange={(e) => setEditData({ ...editData, origem: e.target.value })}
                  >
                    {Object.entries(ORIGEM_LABELS).map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                </div>
                {editData.origem === 'reclamacao_cliente' && (
                  <div className="oc-form-group">
                    <label htmlFor="oc-edit-reclamante">Cliente Reclamante</label>
                    <input
                      id="oc-edit-reclamante"
                      type="text"
                      value={editData.cliente_reclamante}
                      onChange={(e) => setEditData({ ...editData, cliente_reclamante: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div className="oc-form-row">
                <div className="oc-form-group">
                  <label htmlFor="oc-edit-produto">Produto</label>
                  <input
                    id="oc-edit-produto"
                    type="text"
                    value={editData.produto}
                    onChange={(e) => setEditData({ ...editData, produto: e.target.value })}
                  />
                </div>
                <div className="oc-form-group">
                  <label htmlFor="oc-edit-lote">Lote</label>
                  <input
                    id="oc-edit-lote"
                    type="text"
                    value={editData.lote}
                    onChange={(e) => setEditData({ ...editData, lote: e.target.value })}
                  />
                </div>
                <div className="oc-form-group">
                  <label htmlFor="oc-edit-data">Data ocorrência</label>
                  <input
                    id="oc-edit-data"
                    type="date"
                    value={editData.data_ocorrencia || ''}
                    onChange={(e) => setEditData({ ...editData, data_ocorrencia: e.target.value })}
                  />
                </div>
              </div>
              <div className="oc-form-group">
                <label htmlFor="oc-edit-contexto">Situação atual do processo</label>
                <textarea
                  id="oc-edit-contexto"
                  rows={3}
                  value={editData.contexto_processo}
                  onChange={(e) => setEditData({ ...editData, contexto_processo: e.target.value })}
                />
              </div>
              <div className="oc-form-actions" style={{ display: 'flex', gap: '8px' }}>
                <Button icon="save" onClick={handleSalvarEdicao} disabled={acting}>
                  {acting ? 'Salvando…' : 'Salvar'}
                </Button>
                <Button variant="secondary" onClick={() => setEditando(false)} disabled={acting}>
                  Cancelar
                </Button>
              </div>
            </div>
          </section>
        )}

        {!editando && (
        <>
        <section className="oc-detail-section">
          <h3>Identificação</h3>
          <div className="oc-detail-grid">
            <div>
              <label>Origem</label>
              <p>{ORIGEM_LABELS[oc.origem] || oc.origem}</p>
            </div>
            {oc.cliente_reclamante && (
              <div>
                <label>Cliente Reclamante</label>
                <p>{oc.cliente_reclamante}</p>
              </div>
            )}
            {oc.produto && (
              <div>
                <label>Produto</label>
                <p>{oc.produto}</p>
              </div>
            )}
            {oc.lote && (
              <div>
                <label>Lote</label>
                <p>{oc.lote}</p>
              </div>
            )}
            {oc.data_ocorrencia && (
              <div>
                <label>Data Ocorrência</label>
                <p>{formatDate(oc.data_ocorrencia)}</p>
              </div>
            )}
            <div>
              <label>Criada em</label>
              <p>{formatDate(oc.created_at, true)}</p>
            </div>
          </div>
        </section>

        <section className="oc-detail-section">
          <h3>Descrição</h3>
          <p className="oc-detail-text">{oc.descricao}</p>
        </section>

        {oc.contexto_processo && (
          <section className="oc-detail-section">
            <h3>Situação atual do processo</h3>
            <p className="oc-detail-text">{oc.contexto_processo}</p>
          </section>
        )}
        </>
        )}

        {/* #63 Pilar C — Tags estruturadas (manual + sugestão IA).
            Read-only quando promovida (locked). */}
        <OcorrenciaTags ocId={oc.id} locked={isPromovida} />

        {!isPromovida && (
          <section className="oc-detail-actions">
            <h3>Ações</h3>
            <div className="oc-actions-buttons">
              {isEmTriagem && (
                <>
                  <Button
                    variant="secondary"
                    icon="archive"
                    onClick={() => handleMudarStatus('arquivada')}
                    disabled={acting}
                  >
                    Arquivar
                  </Button>
                  <Button
                    variant="secondary"
                    icon="visibility"
                    onClick={() => handleMudarStatus('monitorada')}
                    disabled={acting}
                  >
                    Monitorar
                  </Button>
                </>
              )}
              {oc.status_triagem === 'arquivada' && (
                <Button
                  variant="secondary"
                  icon="undo"
                  onClick={() => handleMudarStatus('em_triagem')}
                  disabled={acting}
                >
                  Reabrir
                </Button>
              )}
              {oc.status_triagem === 'monitorada' && (
                <Button
                  variant="secondary"
                  icon="undo"
                  onClick={() => handleMudarStatus('em_triagem')}
                  disabled={acting}
                >
                  Voltar pra triagem
                </Button>
              )}
              <Button
                variant="primary"
                icon="arrow_upward"
                onClick={() => setConfirmando('promover')}
                disabled={acting}
                title="Cria NC formal com os dados desta ocorrência. Operação irreversível."
              >
                Promover pra NC
              </Button>
            </div>
            <p className="oc-help" style={{ marginTop: '12px', fontSize: '13px' }}>
              <strong>Arquivar:</strong> caso resolvido / não justifica seguir.{' '}
              <strong>Monitorar:</strong> observar antes de decidir.{' '}
              <strong>Promover:</strong> abrir NC formal com análise IA.
            </p>
          </section>
        )}

        {/* #156 — Trilha de auditoria: último campo do detalhe (espelha a NC).
            Só renderiza se há eventos (ocorrências legadas pré-#156 ficam NULL). */}
        {Array.isArray(oc.historico_edicoes) && oc.historico_edicoes.length > 0 && (
          <section className="oc-detail-section oc-hist-section">
            <h3>
              <span className="material-symbols-outlined oc-hist-h-icon">history</span>
              Histórico
            </h3>
            <div className="oc-hist-timeline">
              {oc.historico_edicoes.map((ev, i) => {
                const last = i === oc.historico_edicoes.length - 1;
                const detalhe = prettifyDetalhe(ev);
                return (
                  <div key={i} className="oc-hist-item">
                    <div className="oc-hist-marker">
                      <span className={`oc-hist-icon oc-hist-icon--${ev.tipo}`}>
                        <span className="material-symbols-outlined">
                          {TIPO_ICONS[ev.tipo] || 'history'}
                        </span>
                      </span>
                      {!last && <div className="oc-hist-line" />}
                    </div>
                    <div className="oc-hist-content">
                      <div className="oc-hist-header">
                        <span className="oc-hist-tipo">{TIPO_LABELS[ev.tipo] || ev.tipo}</span>
                        <span className="oc-hist-date">{formatDate(ev.timestamp, true)}</span>
                      </div>
                      {detalhe && <div className="oc-hist-detalhe">{detalhe}</div>}
                      {ev.usuario && (
                        <div className="oc-hist-user">
                          <span className="material-symbols-outlined oc-hist-inline-icon">person</span>
                          {ev.usuario}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* #204 — confirmações estilizadas (copy preservada do #203/#55) */}
      <ConfirmModal
        open={confirmando === 'promover'}
        title="Promover para NC formal?"
        icon="arrow_upward"
        confirmLabel="Promover"
        loading={acting}
        onConfirm={handlePromover}
        onCancel={() => setConfirmando(null)}
      >
        <p>Uma NC será criada em <strong>rascunho</strong> com os dados desta ocorrência. A ocorrência ficará travada (não editável) e linkada à NC criada.</p>
        <p>Você será levado à NC — use o botão <strong>"Analisar com IA"</strong> para gerar a análise.</p>
      </ConfirmModal>

      <ConfirmModal
        open={confirmando === 'excluir'}
        title="Excluir esta ocorrência?"
        icon="delete"
        confirmLabel="Excluir"
        danger
        loading={acting}
        onConfirm={handleExcluir}
        onCancel={() => setConfirmando(null)}
      >
        <p>Ela some da lista mas continua registrada internamente (soft-delete) — coerente com a trilha probatória.</p>
        <p>Ocorrências promovidas pra NC não podem ser excluídas.</p>
      </ConfirmModal>
    </div>
  );
}

export default OcorrenciaDetail;
